import React, { useState, useRef, useEffect } from "react";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams } from "react-router-dom";
import { useLoadScript } from "@react-google-maps/api";
import { Addresses } from "types";
import { addressLine1Validation, cityValidation, countryValidation, firstNameValidation, lastNameValidation, phoneValidation, stateValidation, zipcodeValidation } from "validationSchemas/common";
import AddressAutocomplete from "components/AddressAutocomplete";
import { getAuthCookie } from "../../utils/authCookies";

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const SignageEditAddress = () => {
  const { addressId } = useParams<{ addressId: string }>();
  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress
  const navigate = useNavigate();
  const token = getAuthCookie("sessionToken");
  const [addressObject, setAddressObject] = useState<Addresses | null>(null);
  const [loading, setLoading] = useState(true);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [address, setAddress] = useState("");

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Error fetching address data. Please try again later.";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        setAddressObject(result);
      } catch (err) {
        console.error("Error fetching address data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (addressId) fetchAddress();
  }, [addressId, token]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label_of_address: addressObject?.label || "",
      firstName: addressObject?.first_name || "",
      lastName: addressObject?.last_name || "",
      company: addressObject?.company || "",
      addressLine1: addressObject?.address_details.addressLine1 || "",
      addressLine2: addressObject?.address_details.addressLine2 || "",
      city: addressObject?.address_details.city || "",
      state: addressObject?.address_details.state || "",
      zipcode: addressObject?.address_details.zip || "",
      country: addressObject?.address_details.country || "",
      phone: addressObject?.address_details.phone || "",
      latitude: addressObject?.address_details.latitude || 0,
      longitude: addressObject?.address_details.longitude || 0,
      cityCode: addressObject?.address_details.cityCode || "",
      stateCode: addressObject?.address_details.stateCode || "",
      countryCode: addressObject?.address_details.countryCode || "",
    },
    validationSchema: Yup.object({
      label_of_address: Yup.string().required("Label of address is required"),
      firstName: firstNameValidation,
      lastName: lastNameValidation,
      addressLine1: addressLine1Validation,
      city: cityValidation,
      state: stateValidation,
      zipcode: zipcodeValidation,
      country: countryValidation,
      phone: phoneValidation,
    }),
    onSubmit: async (values) => {

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/addresses/${addressId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              label: values.label_of_address,
              first_name: values.firstName,
              last_name: values.lastName,
              company: values.company,
              address_details: {
                label: values.label_of_address,
                addressLine1: values.addressLine1,
                addressLine2: values.addressLine2,
                city: values.city,
                state: values.state,
                zip: values.zipcode,
                country: values.country,
                phone: values.phone,
                cityCode: values.cityCode,
                stateCode: values.stateCode,
                countryCode: values.countryCode,
                latitude: values.latitude,
                longitude: values.longitude,
              },
            }),
          });

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          console.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }

        showSuccess("Address updated successfully!");
        navigate("/signage/addresses");
      } catch (error: any) {
        console.error(error);
      } finally {
        setFormSubmitting(false);
        setUploadProgress(0);
      }
    }
  });

  const clearGoogleAddressFields = () => {
    formik.setFieldValue("city", "", false);
    formik.setFieldValue("cityCode", "", false);
    formik.setFieldValue("state", "", false);
    formik.setFieldValue("stateCode", "", false);
    formik.setFieldValue("zipcode", "", false);
    formik.setFieldValue("country", "", false);
    formik.setFieldValue("countryCode", "", false);
    formik.setFieldValue("latitude", 0, false);
    formik.setFieldValue("longitude", 0, false);
  };

  const handleAddressLine1Change = (value: string) => {
    formik.setFieldValue("addressLine1", value);
    if (!value.trim()) {
      clearGoogleAddressFields();
    }
  };

  return (

    <div className="content">
      <div className="col-md-12 d-flex justify-content-center">
        <div className="page-container col-md-9">
          <h3 className="m-0 text-center fw-bold addheaders">Edit Address</h3>
        </div>
      </div>
      <div className="row ">
        <div className="col-md-12 d-flex justify-content-center">
          <div className="col-md-9">
            <div className="card-body p-1">
              <div className="p-2 px-4 mt-1">
                <form onSubmit={formik.handleSubmit}>
                  <div className=" row cust-margin-4">
                    <div>
                      <h5>Address Details</h5>
                      <hr className="lines" style={{ width: "110px" }} />
                    </div>
                    <div className="col-md-6 " >
                      <label className="form-label">
                        Label of address  <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="label_of_address"
                        name="label_of_address"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.label_of_address}
                      />
                      {formik.touched.label_of_address && formik.errors.label_of_address ? (
                        <div className="text-danger">
                          {formik.errors.label_of_address}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="firstName"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                      />
                      {formik.touched.firstName && formik.errors.firstName ? (
                        <div className="text-danger">
                          {formik.errors.firstName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="lastName"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                      />
                      {formik.touched.lastName && formik.errors.lastName ? (
                        <div className="text-danger">
                          {formik.errors.lastName}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Company
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="company"
                        name="company"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.company}
                      />
                      {formik.touched.company && formik.errors.company ? (
                        <div className="text-danger">
                          {formik.errors.company}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Phone number  <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="phone"
                        name="phone"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.phone}
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Address Line 1 <span className="text-danger">*</span>
                      </label>
                      <AddressAutocomplete
                        id="addressLine1"
                        name="addressLine1"
                        value={formik.values.addressLine1}
                        onChange={handleAddressLine1Change}
                        onSelect={(picked) => {
                          formik.setFieldValue("addressLine1", picked.addressLine1);
                          formik.setFieldValue("city", picked.city);
                          formik.setFieldValue("cityCode", picked.cityCode);
                          formik.setFieldValue("state", picked.state);
                          formik.setFieldValue("stateCode", picked.stateCode);
                          formik.setFieldValue("zipcode", picked.zipcode);
                          formik.setFieldValue("country", picked.country);
                          formik.setFieldValue("countryCode", picked.countryCode);
                          formik.setFieldValue("latitude", picked.latitude);
                          formik.setFieldValue("longitude", picked.longitude);
                        }}
                        className={`form-control ${formik.touched.addressLine1 && formik.errors.addressLine1 ? "is-invalid" : ""}`}
                        placeholder="Enter address"
                        error={formik.touched.addressLine1 && formik.errors.addressLine1 ? (formik.errors.addressLine1 as string) : undefined}
                      />
                      {formik.touched.addressLine1 && formik.errors.addressLine1 ? (
                        <div className="text-danger">
                          {formik.errors.addressLine1}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="addressLine2"
                        name="addressLine2"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.addressLine2}
                      />
                      {formik.touched.addressLine2 && formik.errors.addressLine2 ? (
                        <div className="invalid-feedback">
                          {formik.errors.addressLine2}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.city}
                        disabled
                      />
                      {formik.touched.city && formik.errors.city ? (
                        <div className="text-danger">
                          {formik.errors.city}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        State <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="state"
                        name="state"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.state}
                        disabled
                      />
                      {formik.touched.state && formik.errors.state ? (
                        <div className="text-danger">
                          {formik.errors.state}
                        </div>
                      ) : null}
                    </div>
                    <div className="col-md-6 ">
                      <label className="form-label">
                        Zip Code <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="zipcode"
                        name="zipcode"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.zipcode}
                        disabled
                      />
                      {formik.touched.zipcode && formik.errors.zipcode ? (
                        <div className="text-danger">
                          {formik.errors.zipcode}
                        </div>
                      ) : null}
                    </div> <div className="col-md-6 ">
                      <label className="form-label">
                        Country <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="country"
                        name="country"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.country}
                        disabled
                      />
                      {formik.touched.country && formik.errors.country ? (
                        <div className="text-danger">
                          {formik.errors.country}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="row mb-3 rcust-margin-4">
                    <div className="col-md-12 d-flex justify-content-between">
                      <button
                        className="btn btn-outline-secondary br-8 backbutton"
                        type="button"
                        onClick={() => navigate(-1)}
                      >
                        Back
                      </button>

                      <button className="btn btn-primary submit-btn" type="submit">
                        Save
                      </button>
                    </div>
                  </div>
                  {/* <div className="rcust-margin-4 justify-content-end  mb-3 row text-end">
                    <div className="col-md-12">
                      <button className="btn btn-primary submit-btn" type="submit">
                        Update
                      </button>
                    </div>
                  </div> */}

                </form>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
    //   </div>
    // </div>
  );
};

export default SignageEditAddress;
